import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LogOut, RefreshCw, Key, Users, BarChart3, Plus, Trash2, Power, Copy, Check, TrendingUp, Shield } from 'lucide-react';
import { analyticsEvents } from '../services/vercelAnalytics';

const API_BASE = 'https://aifraudapi.onrender.com';

interface ApiKeyRecord {
  key: string;
  name: string;
  email?: string;
  createdAt: string;
  lastUsedAt?: string;
  isActive: boolean;
  requestCount: number;
  plan: 'free' | 'basic' | 'pro' | 'enterprise';
  rateLimit: number;
  monthlyLimit: number;
  monthlyUsage: number;
}

interface DashboardData {
  summary: {
    totalKeys: number;
    activeKeys: number;
    inactiveKeys: number;
    totalRequests: number;
    requestsToday: number;
    requestsThisMonth: number;
    avgResponseTime: number;
    cacheHitRate: number;
  };
  planDistribution: {
    free: number;
    basic: number;
    pro: number;
    enterprise: number;
  };
  topUsers: {
    name: string;
    plan: string;
    requestCount: number;
    lastUsed?: string;
  }[];
  usageByDay: { date: string; count: number }[];
}

const SESSION_KEY = 'nomorai_admin_session';

export const AdminPanel: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'keys' | 'create'>('dashboard');
  const [apiKeys, setApiKeys] = useState<ApiKeyRecord[]>([]);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newKeyForm, setNewKeyForm] = useState({ name: '', email: '', plan: 'free' as 'free' | 'basic' | 'pro' | 'enterprise' });
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeader = () => ({
    'Authorization': `Admin ${storedPassword}`,
    'Content-Type': 'application/json'
  });

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const savedPassword = sessionStorage.getItem(SESSION_KEY);
      if (savedPassword) {
        try {
          const response = await fetch(`${API_BASE}/v1/admin/dashboard`, {
            headers: { 'Authorization': `Admin ${savedPassword}` }
          });
          if (response.ok) {
            setIsAuthenticated(true);
            setStoredPassword(savedPassword);
            const data = await response.json();
            setDashboard(data);
          } else {
            sessionStorage.removeItem(SESSION_KEY);
          }
        } catch (err) {
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
      setIsInitializing(false);
    };
    checkSession();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Test the password by fetching dashboard
      const response = await fetch(`${API_BASE}/v1/admin/dashboard`, {
        headers: { 'Authorization': `Admin ${password}` }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        setStoredPassword(password);
        sessionStorage.setItem(SESSION_KEY, password);
        analyticsEvents.adminLoginSuccess();
        const data = await response.json();
        setDashboard(data);
      } else {
        setError('Invalid password');
        analyticsEvents.adminLoginFailed();
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDashboard = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/admin/dashboard`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadApiKeys = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v1/admin/keys`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.keys || []);
      }
    } catch (err) {
      console.error('Failed to load API keys:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const createApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/v1/admin/keys`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(newKeyForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCreatedKey(data.key);
        setNewKeyForm({ name: '', email: '', plan: 'free' });
        loadApiKeys();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to create API key');
      }
    } catch (err) {
      setError('Failed to create API key');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleKeyStatus = async (keyPrefix: string, isActive: boolean) => {
    try {
      const action = isActive ? 'deactivate' : 'reactivate';
      await fetch(`${API_BASE}/v1/admin/keys/${keyPrefix}/${action}`, {
        method: 'PATCH',
        headers: getAuthHeader()
      });
      loadApiKeys();
    } catch (err) {
      console.error('Failed to toggle key status:', err);
    }
  };

  const deleteKey = async (keyPrefix: string) => {
    if (!window.confirm('Are you sure you want to delete this API key? This cannot be undone.')) return;
    
    try {
      await fetch(`${API_BASE}/v1/admin/keys/${keyPrefix}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      loadApiKeys();
    } catch (err) {
      console.error('Failed to delete key:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
    setPassword('');
    setStoredPassword('');
    setApiKeys([]);
    setDashboard(null);
  };

  const handleRefresh = () => {
    if (activeTab === 'dashboard') loadDashboard();
    if (activeTab === 'keys') loadApiKeys();
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'dashboard') loadDashboard();
      if (activeTab === 'keys') loadApiKeys();
    }
  }, [activeTab, isAuthenticated]);

  const planColors = {
    free: 'bg-gray-100 text-gray-700',
    basic: 'bg-blue-100 text-blue-700',
    pro: 'bg-purple-100 text-purple-700',
    enterprise: 'bg-amber-100 text-amber-700'
  };

  // Show loading while checking session
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F1F4F8] to-[#E5EDFF] flex items-center justify-center p-4">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#1F4FFF] animate-spin mx-auto mb-4" />
          <p className="text-[#475467]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F1F4F8] to-[#E5EDFF] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl w-full max-w-md"
        >
          <div className="flex justify-center mb-4">
            <Shield className="w-12 h-12 text-[#1F4FFF]" />
          </div>
          <h1 className="text-3xl font-bold text-[#0A1A2F] mb-2 text-center">NoMoreAI</h1>
          <p className="text-center text-[#475467] mb-8">Admin Panel</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A1A2F] mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-[#D0D5DD] rounded-xl focus:ring-2 focus:ring-[#1F4FFF] focus:border-transparent"
                placeholder="Enter admin password"
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-3 bg-[#1F4FFF] text-white font-semibold rounded-xl hover:bg-[#163ACC] transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <p className="text-xs text-center text-[#475467] mt-4">
            Secure admin access only
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F1F4F8] to-[#E5EDFF] p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold text-[#0A1A2F]">Admin Dashboard</h1>
            <p className="text-[#475467] mt-1">API Key Management & Analytics</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-[#1F4FFF] text-white rounded-xl hover:bg-[#163ACC] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-[#D0D5DD] rounded-xl hover:bg-gray-50 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'keys', label: 'API Keys', icon: Key },
            { id: 'create', label: 'Create Key', icon: Plus }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#1F4FFF] text-white'
                  : 'bg-white text-[#475467] hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#D0D5DD]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#475467]">Total API Keys</p>
                    <p className="text-3xl font-bold text-[#0A1A2F] mt-2">{dashboard.summary.totalKeys}</p>
                  </div>
                  <Key className="w-10 h-10 text-[#1F4FFF] opacity-20" />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#D0D5DD]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#475467]">Active Keys</p>
                    <p className="text-3xl font-bold text-[#16A34A] mt-2">{dashboard.summary.activeKeys}</p>
                  </div>
                  <Users className="w-10 h-10 text-[#16A34A] opacity-20" />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#D0D5DD]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#475467]">Requests Today</p>
                    <p className="text-3xl font-bold text-[#0A1A2F] mt-2">{dashboard.summary.requestsToday}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-[#FACC15] opacity-20" />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#D0D5DD]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#475467]">Cache Hit Rate</p>
                    <p className="text-3xl font-bold text-[#0A1A2F] mt-2">{dashboard.summary.cacheHitRate}%</p>
                  </div>
                  <BarChart3 className="w-10 h-10 text-[#1F4FFF] opacity-20" />
                </div>
              </div>
            </div>

            {/* Plan Distribution */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#D0D5DD]">
              <h3 className="text-lg font-semibold text-[#0A1A2F] mb-4">Plan Distribution</h3>
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(dashboard.planDistribution).map(([plan, count]) => (
                  <div key={plan} className="text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${planColors[plan as keyof typeof planColors]}`}>
                      {plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </span>
                    <p className="text-2xl font-bold text-[#0A1A2F] mt-2">{count}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Users */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#D0D5DD]">
              <h3 className="text-lg font-semibold text-[#0A1A2F] mb-4">Top Users</h3>
              <div className="space-y-3">
                {dashboard.topUsers.map((user, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#F1F4F8] rounded-xl">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-8 bg-[#1F4FFF] text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-medium text-[#0A1A2F]">{user.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${planColors[user.plan as keyof typeof planColors]}`}>
                          {user.plan}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#0A1A2F]">{user.requestCount.toLocaleString()}</p>
                      <p className="text-xs text-[#475467]">requests</p>
                    </div>
                  </div>
                ))}
                {dashboard.topUsers.length === 0 && (
                  <p className="text-center text-[#475467] py-4">No usage data yet</p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'keys' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-sm border border-[#D0D5DD] overflow-hidden"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#D0D5DD]">
              <h2 className="text-xl font-semibold text-[#0A1A2F]">API Keys ({apiKeys.length})</h2>
              <button
                onClick={loadApiKeys}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-[#1F4FFF] text-white rounded-xl hover:bg-[#163ACC] transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>

            <div className="divide-y divide-[#D0D5DD]">
              {apiKeys.map((key) => (
                <div key={key.key} className="p-6 hover:bg-[#F1F4F8] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <p className="font-semibold text-[#0A1A2F]">{key.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${planColors[key.plan]}`}>
                          {key.plan}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${key.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {key.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-[#475467] font-mono mb-2">{key.key}</p>
                      {key.email && <p className="text-sm text-[#475467]">{key.email}</p>}
                      <div className="flex items-center space-x-4 mt-2 text-xs text-[#475467]">
                        <span>Created: {new Date(key.createdAt).toLocaleDateString()}</span>
                        {key.lastUsedAt && <span>Last used: {new Date(key.lastUsedAt).toLocaleDateString()}</span>}
                        <span>Requests: {key.requestCount.toLocaleString()}</span>
                        <span>Monthly: {key.monthlyUsage}/{key.monthlyLimit || '∞'}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleKeyStatus(key.key.split('...')[0], key.isActive)}
                        className={`p-2 rounded-lg transition-colors ${key.isActive ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                        title={key.isActive ? 'Deactivate' : 'Reactivate'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteKey(key.key.split('...')[0])}
                        className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {apiKeys.length === 0 && (
                <div className="p-12 text-center">
                  <Key className="w-12 h-12 text-[#D0D5DD] mx-auto mb-4" />
                  <p className="text-[#475467]">No API keys yet</p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="mt-4 px-4 py-2 bg-[#1F4FFF] text-white rounded-xl hover:bg-[#163ACC] transition-colors"
                  >
                    Create First Key
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Create Key Tab */}
        {activeTab === 'create' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-xl mx-auto"
          >
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#D0D5DD]">
              <h2 className="text-2xl font-bold text-[#0A1A2F] mb-6">Create New API Key</h2>

              {createdKey ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-green-700 font-medium mb-2">API Key Created Successfully!</p>
                    <p className="text-sm text-green-600 mb-4">
                      Copy this key now. You won't be able to see it again.
                    </p>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 bg-white px-4 py-3 rounded-lg font-mono text-sm border border-green-200 break-all">
                        {createdKey}
                      </code>
                      <button
                        onClick={() => copyToClipboard(createdKey)}
                        className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        {copiedKey ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCreatedKey(null);
                      setActiveTab('keys');
                    }}
                    className="w-full px-4 py-3 bg-[#1F4FFF] text-white font-semibold rounded-xl hover:bg-[#163ACC] transition-colors"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={createApiKey} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A1A2F] mb-2">
                      Name / Description *
                    </label>
                    <input
                      type="text"
                      value={newKeyForm.name}
                      onChange={(e) => setNewKeyForm({ ...newKeyForm, name: e.target.value })}
                      className="w-full px-4 py-3 border border-[#D0D5DD] rounded-xl focus:ring-2 focus:ring-[#1F4FFF] focus:border-transparent"
                      placeholder="e.g., Production App, Test Key"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1A2F] mb-2">
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={newKeyForm.email}
                      onChange={(e) => setNewKeyForm({ ...newKeyForm, email: e.target.value })}
                      className="w-full px-4 py-3 border border-[#D0D5DD] rounded-xl focus:ring-2 focus:ring-[#1F4FFF] focus:border-transparent"
                      placeholder="user@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1A2F] mb-2">
                      Plan
                    </label>
                    <select
                      value={newKeyForm.plan}
                      onChange={(e) => setNewKeyForm({ ...newKeyForm, plan: e.target.value as typeof newKeyForm.plan })}
                      className="w-full px-4 py-3 border border-[#D0D5DD] rounded-xl focus:ring-2 focus:ring-[#1F4FFF] focus:border-transparent"
                    >
                      <option value="free">Free (10 req/min, 100/month)</option>
                      <option value="basic">Basic (50 req/min, 2,000/month)</option>
                      <option value="pro">Pro (100 req/min, 10,000/month)</option>
                      <option value="enterprise">Enterprise (500 req/min, 100000+/unlimited)</option>
                    </select>
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !newKeyForm.name}
                    className="w-full px-4 py-3 bg-[#1F4FFF] text-white font-semibold rounded-xl hover:bg-[#163ACC] transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Creating...' : 'Create API Key'}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-[#475467]">
          <p>Last updated: {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};
